import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

const DESKTOP_BREAKPOINT = 750;
const GROUP_SIZE_DESKTOP = 8;
const GROUP_SIZE_MOBILE = 4;
// Divisible by both group sizes above, so a fetched page never leaves a partial bento group behind.
const PAGE_SIZE = 32;
const LOAD_MORE_THRESHOLD = 3;

/**
 * Renders a Yotpo "Album" (Instagram UGC) grid with a lightbox modal, backed by the theme's
 * native <slideshow-component> and <dialog-component> instead of Glider.js/MicroModal.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} gridWrap
 * @property {HTMLTemplateElement} [gridArrowsTemplate]
 * @property {HTMLElement & {showDialog: Function, closeDialog: Function, refs: {modalWrap: HTMLElement, modalArrowsTemplate?: HTMLTemplateElement}}} modalDialogComponent
 *
 * @extends {Component<Refs>}
 */
export class YotpoAlbum extends Component {
  // modalWrap/modalArrowsTemplate live inside <dialog-component>, which is itself a Component
  // boundary, so they land in modalDialogComponent.refs rather than this component's own refs.
  requiredRefs = ['gridWrap', 'modalDialogComponent'];

  /** @type {any[]} */
  #items = [];
  #page = 1;
  #loading = false;
  #exhausted = false;
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  #media = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
  /** @type {any} slideshow-component instance for the grid, created once data has loaded */
  #gridSlideshow = null;
  /** @type {any} slideshow-component instance for the modal lightbox */
  #modalSlideshow = null;
  /**
   * Stable reference to the modal dialog-component, captured once on connect. Moving it to
   * <body> takes it out of this component's own subtree, which would otherwise drop it from
   * the ref-tracking system (Component only tracks refs that stay descendants), so everything
   * below reaches it through this field instead of `this.refs.modalDialogComponent`.
   * @type {any}
   */
  #dialogComponent = null;
  /** @type {Node | null} where the modal dialog-component lives when it's not in <body> */
  #dialogHomeParent = null;
  /** @type {Node | null} */
  #dialogHomeNextSibling = null;

  connectedCallback() {
    super.connectedCallback();

    this.#dialogComponent = this.refs.modalDialogComponent;

    this.addEventListener('click', this.#handleClick);
    this.#media.addEventListener('change', this.#handleDeviceChange);
    // Moving the dialog to <body> while open keeps it clear of this section's own stacking
    // context/overflow; move it back once it's closed, whichever way that happens (close
    // button, backdrop click, Escape all funnel through closeDialog()).
    this.#dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    // Once moved, the dialog is no longer a descendant of `this`, so clicks inside it (the
    // tagged-products switcher) would stop bubbling up to the listener above. Bind directly to
    // the dialog-component itself instead — that listener travels with it wherever it's parented.
    this.#dialogComponent.addEventListener('click', this.#handleDialogClick);

    // Load the first page once the album actually scrolls into view.
    this.#visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#visibilityObserver?.disconnect();
      this.#visibilityObserver = null;
      this.#loadNextPage();
    });
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.#media.removeEventListener('change', this.#handleDeviceChange);
    this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#dialogComponent?.removeEventListener('click', this.#handleDialogClick);
    this.#visibilityObserver?.disconnect();
    // Don't leave the dialog orphaned in <body> if this component is torn down while it's open.
    this.#handleDialogClose();
  }

  get #apiKey() {
    return this.dataset.yotpoApiKey ?? '';
  }

  get #albumId() {
    return this.dataset.yotpoAlbumId ?? '';
  }

  get #enableShopButton() {
    return this.dataset.yotpoEnableShopButton === 'true';
  }

  get #viewMoreText() {
    return this.dataset.yotpoViewmoreText || 'View more';
  }

  get #isDesktop() {
    return this.#media.matches;
  }

  #groupSize() {
    return this.#isDesktop ? GROUP_SIZE_DESKTOP : GROUP_SIZE_MOBILE;
  }

  /**
   * @param {number} page
   * @param {number} [perPage]
   * @returns {URL | null}
   */
  #buildRequestUrl(page, perPage = PAGE_SIZE) {
    const apiKey = this.#apiKey;
    const albumId = this.#albumId;
    if (!apiKey || !albumId) return null;

    const requestUrl = new URL(`https://api.yotpo.com/v1/widget/${apiKey}/albums/by_name`);
    requestUrl.searchParams.set('album_name', albumId);
    requestUrl.searchParams.set('per_page', String(perPage));
    requestUrl.searchParams.set('page', String(page));
    return requestUrl;
  }

  /** @param {number} page */
  async #fetchPage(page) {
    const requestUrl = this.#buildRequestUrl(page);
    if (!requestUrl) return null;

    try {
      const response = await fetch(requestUrl.href, { method: 'GET', redirect: 'follow' });
      const data = await response.json();
      if (data?.status?.code !== 200) {
        console.error('[yotpo-album]', data);
        return null;
      }
      return data.response;
    } catch (error) {
      console.error('[yotpo-album]', error);
      return null;
    }
  }

  async #loadNextPage() {
    if (this.#loading || this.#exhausted) return;
    this.#loading = true;

    const result = await this.#fetchPage(this.#page);
    const newItems = result?.images ?? [];

    if (!newItems.length) {
      this.#exhausted = true;
      this.#loading = false;
      return;
    }

    this.#page += 1;
    this.#items.push(...newItems);

    if (this.#gridSlideshow) {
      this.#appendGridGroups(newItems);
    } else {
      this.#createGridSlideshow();
    }

    if (this.#modalSlideshow) {
      this.#appendModalSlides(newItems);
    } else {
      this.#createModalSlideshow();
    }

    this.#loading = false;
  }

  #handleDeviceChange = () => {
    // Crossing the breakpoint changes the bento group size (4 vs 8 items per slide), so the
    // existing grid slides need to be regrouped from scratch. The modal is unaffected (1 slide
    // per item regardless of breakpoint).
    this.#rebuildGridSlides();
  };

  /** @param {SlideshowSelectEvent} event */
  #handleSlideshowSelect = (event) => {
    const slideshow = event.currentTarget;
    const total = slideshow?.slides?.length ?? 0;
    if (event.detail.index >= total - LOAD_MORE_THRESHOLD) this.#loadNextPage();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const item = target.closest('.yotpo-album__item[data-image-id]');
    if (item instanceof HTMLElement && item.dataset.imageId) {
      this.#openModal(item.dataset.imageId);
    }
  };

  /**
   * Handles clicks inside the modal dialog specifically (see the listener setup in
   * connectedCallback for why this is separate from #handleClick).
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const taggedItem = target.closest('.tagged-list-item');
    if (taggedItem instanceof HTMLElement) {
      this.#handleTaggedProductClick(taggedItem);
    }
  };

  /** @param {HTMLElement} taggedItem */
  #handleTaggedProductClick(taggedItem) {
    const container = taggedItem.closest('.modal--yotpo-album__tagged-products');
    if (!container) return;

    const link = taggedItem.getAttribute('data-tagged-link') ?? '';
    const name = taggedItem.getAttribute('data-tagged-name') ?? '';
    const image = taggedItem.getAttribute('data-tagged-image') ?? '';

    const linkEl = container.querySelector('[data-tagged-product-link]');
    const nameEl = container.querySelector('[data-tagged-product-name]');
    const imageEl = container.querySelector('[data-tagged-product-image]');

    if (linkEl instanceof HTMLAnchorElement) linkEl.href = link;
    if (nameEl) nameEl.textContent = name;
    if (imageEl instanceof HTMLImageElement) imageEl.src = image;
  }

  /** @param {string} imageId */
  #openModal(imageId) {
    if (!this.#modalSlideshow) return;

    const dialogComponent = this.#dialogComponent;
    this.#moveDialogToBody(dialogComponent);

    const selectClickedSlide = () => this.#modalSlideshow?.select({ id: imageId }, undefined, { animate: false });

    // showDialog() defers the actual dialog.showModal() to a requestAnimationFrame, so the
    // slideshow inside is still display:none (0 dimensions) right after calling it. Waiting for
    // dialog:open — dispatched right after showModal() actually runs — guarantees select()'s
    // scroll-into-view math runs against a laid-out, visible slideshow instead of guessing at
    // frame timing.
    dialogComponent.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        selectClickedSlide();

        // slideshow.js's own #setupSlideshow() queues a one-time "nothing is visible yet, default
        // to slide 0" correction (via a shared scheduler + requestAnimationFrame) as soon as the
        // slideshow is first created — for us that's back when the album data loaded, long before
        // the dialog was ever opened. If that correction hasn't resolved yet by the time this
        // click happens, it can fire moments later and stomp select()'s work — resetting current
        // and scrollLeft back to slide 0, not just aria-hidden. Re-run the same select() a few
        // times over the next second so the clicked image wins regardless of exactly when that
        // settles; select() is a cheap no-op once the current slide already matches.
        [50, 200, 600, 1200].forEach((delay) => setTimeout(selectClickedSlide, delay));
      },
      { once: true }
    );
    dialogComponent.showDialog();
  }

  /**
   * Moves the modal dialog-component to be a direct child of <body> so it renders clear of this
   * section's own stacking context/overflow, remembering where it came from.
   *
   * @param {Element} dialogComponent
   */
  #moveDialogToBody(dialogComponent) {
    if (dialogComponent.parentNode === document.body) return;

    this.#dialogHomeParent = dialogComponent.parentNode;
    this.#dialogHomeNextSibling = dialogComponent.nextSibling;
    document.body.append(dialogComponent);
  }

  /** Restores the modal dialog-component to its original spot in the section markup. */
  #handleDialogClose = () => {
    const dialogComponent = this.#dialogComponent;
    if (!this.#dialogHomeParent || dialogComponent.parentNode !== document.body) return;

    this.#dialogHomeParent.insertBefore(dialogComponent, this.#dialogHomeNextSibling);
    this.#dialogHomeParent = null;
    this.#dialogHomeNextSibling = null;
  };

  // ---- Grid ----

  #createGridSlideshow() {
    const groups = chunk(this.#items, this.#groupSize());
    const slidesHtml = groups.map((group, index) => this.#renderGridSlide(group, index === 0)).join('');
    const arrows = this.refs.gridArrowsTemplate?.content.cloneNode(true);

    const shell = createSlideshowElement(slidesHtml, arrows, 'yotpo-album__grid yotpo-album__grid--desktop');
    this.refs.gridWrap.append(shell);

    this.#gridSlideshow = shell;
    shell.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
  }

  /** @param {any[]} newItems */
  #appendGridGroups(newItems) {
    const groups = chunk(newItems, this.#groupSize());
    const html = groups.map((group) => this.#renderGridSlide(group, false)).join('');
    this.#gridSlideshow.refs.scroller.insertAdjacentHTML('beforeend', html);
  }

  #rebuildGridSlides() {
    if (!this.#gridSlideshow) return;

    const groups = chunk(this.#items, this.#groupSize());
    const html = groups.map((group, index) => this.#renderGridSlide(group, index === 0)).join('');
    const { scroller } = this.#gridSlideshow.refs;
    scroller.replaceChildren();
    scroller.insertAdjacentHTML('beforeend', html);
    this.#gridSlideshow.select(0, undefined, { animate: false });
  }

  /**
   * @param {any[]} items
   * @param {boolean} visible
   */
  #renderGridSlide(items, visible) {
    const itemsHtml = items.map((item, index) => this.#renderGridItem(item, index + 1)).join('');
    return `<slideshow-slide ref="slides[]" aria-hidden="${visible ? 'false' : 'true'}"><div class="yotpo-album__grid__slide">${itemsHtml}</div></slideshow-slide>`;
  }

  /**
   * @param {any} item
   * @param {number} positionInGroup - 1-based position within its bento group.
   */
  #renderGridItem(item, positionInGroup) {
    const modifier = positionInGroup <= 2 ? 'large' : 'small';
    return `
      <div class="yotpo-album__item yotpo-album__item--${modifier}" data-image-id="${escapeHtml(item.external_media_id)}">
        <img alt="" class="yotpo-album__item__image" src="${escapeHtml(item.original_image_url)}" loading="lazy">
      </div>
    `;
  }

  // ---- Modal ----

  #createModalSlideshow() {
    const { modalWrap, modalArrowsTemplate } = this.#dialogComponent.refs;
    const slidesHtml = this.#items.map((item, index) => this.#renderModalSlide(item, index === 0)).join('');
    const arrows = modalArrowsTemplate?.content.cloneNode(true);

    const shell = createSlideshowElement(slidesHtml, arrows, 'glider-yopto-insta');
    modalWrap.append(shell);

    this.#modalSlideshow = shell;
    shell.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
  }

  /** @param {any[]} newItems */
  #appendModalSlides(newItems) {
    const html = newItems.map((item) => this.#renderModalSlide(item, false)).join('');
    this.#modalSlideshow.refs.scroller.insertAdjacentHTML('beforeend', html);
  }

  /**
   * @param {any} item
   * @param {boolean} visible
   */
  #renderModalSlide(item, visible) {
    const taggedProductsHtml = this.#enableShopButton ? this.#renderTaggedProducts(item.tagged_products) : '';

    return `
      <slideshow-slide ref="slides[]" slide-id="${escapeHtml(item.external_media_id)}" aria-hidden="${visible ? 'false' : 'true'}">
        <div class="modal--yotpo-album__wrapper">
          <div class="modal--yotpo-album__img-wrapper">
            <img src="${escapeHtml(item.original_image_url)}" alt="" loading="lazy">
          </div>
          <div class="modal--yotpo-album__info">
            ${taggedProductsHtml}
            <div class="modal--yotpo-album__user"><p>${escapeHtml(item.post?.username)}</p></div>
            <div class="modal--yotpo-album__content"><p>${escapeHtml(item.post?.content)}</p></div>
          </div>
        </div>
      </slideshow-slide>
    `;
  }

  /** @param {any[] | undefined} taggedProducts */
  #renderTaggedProducts(taggedProducts) {
    if (!taggedProducts?.length) return '';

    const [first, ...rest] = taggedProducts;

    let html = `
      <div class="modal--yotpo-album__tagged-products">
        <div class="modal--yotpo-album__tagged-product">
          <img class="modal--yotpo-album__tagged-product__image" data-tagged-product-image src="${escapeHtml(first.image_url)}" alt="" loading="lazy">
          <div class="modal--yotpo-album__tagged-product__info">
            <p class="modal--yotpo-album__tagged-product__title h6" data-tagged-product-name>${escapeHtml(first.name?.split(' - ')[0])}</p>
            <a class="modal--yotpo-album__tagged-product__btn button button--primary" data-tagged-product-link href="${escapeHtml(first.product_link)}">${escapeHtml(this.#viewMoreText)}</a>
          </div>
        </div>
    `;

    if (rest.length) {
      html += '<ul class="modal--yotpo-album__tagged-list">';
      html += taggedProducts
        .map(
          (product, index) => `
            <li
              class="tagged-list-item"
              data-index="${index}"
              data-tagged-link="${escapeHtml(product.product_link)}"
              data-tagged-name="${escapeHtml(product.name?.split(' - ')[0])}"
              data-tagged-image="${escapeHtml(product.image_url)}"
            >
              <img src="${escapeHtml(product.image_url)}" loading="lazy">
            </li>
          `
        )
        .join('');
      html += '</ul>';
    }

    html += '</div>';
    return html;
  }
}

/**
 * @param {any[]} items
 * @param {number} size
 */
function chunk(items, size) {
  const groups = [];
  for (let i = 0; i < items.length; i += size) {
    groups.push(items.slice(i, i + size));
  }
  return groups;
}

/**
 * Assembles a detached <slideshow-component> (slides + cloned arrows included) so that by the
 * time it's inserted into the document, connectedCallback sees the real slide count up front
 * instead of the 0/1-slide "without controls" path (which never sets up drag/select support).
 *
 * @param {string} slidesHtml
 * @param {DocumentFragment | undefined} arrows
 * @param {string} className
 */
function createSlideshowElement(slidesHtml, arrows, className) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <slideshow-component class="${className}" initial-slide="0">
      <slideshow-container ref="slideshowContainer">
        <slideshow-slides ref="scroller" tabindex="-1">${slidesHtml}</slideshow-slides>
      </slideshow-container>
    </slideshow-component>
  `;

  const shell = wrapper.firstElementChild;
  if (arrows) shell?.querySelector('slideshow-container')?.prepend(arrows);

  return shell;
}

/** @param {unknown} value */
function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
  );
}

if (!customElements.get('yotpo-album-component')) {
  customElements.define('yotpo-album-component', YotpoAlbum);
}
